/**
 * Live-session API helpers aligned with ExskilenceLMSV2 LiveSessionFactory.
 *
 * Compatibility routes under /goooglemeet/* and /livekit/* remain the FE
 * surface; the backend selects Google Meet vs LiveKit via session_provider.
 */
import { authService } from "../utils/authService";

export type SessionProvider = "google_meet" | "livekit" | string;

export function normalizeSessionProvider(
  value?: string | null
): SessionProvider {
  const p = (value || "").trim().toLowerCase();
  if (p === "livekit") return "livekit";
  if (p === "google_meet" || p === "meet" || p === "google") return "google_meet";
  return p || "google_meet";
}

export function isLiveKitProvider(value?: string | null): boolean {
  return normalizeSessionProvider(value) === "livekit";
}

/** List attendance-capable sessions (optional provider filter). */
export async function fetchSessionAttendances(params: {
  track_id?: string;
  course_id?: string;
  batch_id?: string;
  session_provider?: string;
}) {
  const body: Record<string, string> = {
    track_id: params.track_id || "",
    course_id: params.course_id || "",
    batch_id: params.batch_id || "",
  };
  if (params.session_provider) {
    body.session_provider = normalizeSessionProvider(params.session_provider);
  }
  const response = await authService.post("/session/attendances/", body);
  return response.data;
}

/**
 * Live / historical participant report via factory-backed
 * POST /goooglemeet/live-participants/ (works for Meet + LiveKit).
 */
export async function fetchLiveParticipants(params: {
  session_id: string | number;
  email: string;
  meeting_link?: string | null;
  instructor_name?: string;
}) {
  const response = await authService.post("/goooglemeet/live-participants/", {
    email: params.email,
    meeting_link: params.meeting_link || undefined,
    instructor_name: params.instructor_name,
    session_id: params.session_id,
  });
  return response.data;
}

/** Completed-session participant summary. */
export async function fetchSessionInfo(sessionId: string | number) {
  const response = await authService.post("/goooglemeet/get-session-info/", {
    session_id: sessionId,
  });
  return response.data;
}

/** Sync Meet attendance before end (skip for LiveKit — presence already logged). */
export async function syncParticipantInfoBeforeEnd(params: {
  session_id: string | number;
  email: string;
  meeting_link?: string | null;
  name?: string;
}) {
  const response = await authService.post("/goooglemeet/get-participant-info/", {
    session_id: params.session_id,
    email: params.email,
    meeting_link: params.meeting_link || undefined,
    name: params.name,
  });
  return response.data;
}

export async function endLiveSession(
  sessionId: string | number,
  userId: string
) {
  const uid = String(userId || "").trim();
  if (!uid) {
    throw new Error(
      "Unable to end session: user ID not found. Please sign in again."
    );
  }
  try {
    const response = await authService.get(
      `/session/end-session/${sessionId}/${uid}/`
    );
    const data = response.data as { success?: boolean; message?: string };
    const message = String(data?.message || "").trim();
    if (data?.success === true || /successfully/i.test(message)) {
      return data;
    }
    throw new Error(message || "Failed to end session");
  } catch (err: unknown) {
    const detail = (err as { response?: { data?: { detail?: unknown } } })
      ?.response?.data?.detail;
    if (typeof detail === "string" && detail.trim()) {
      throw new Error(detail.trim());
    }
    if (detail && typeof detail === "object" && detail !== null) {
      const msg = (detail as { message?: unknown }).message;
      if (typeof msg === "string" && msg.trim()) {
        throw new Error(msg.trim());
      }
    }
    throw err;
  }
}

/** Pause an LCM (mandatory) live session until expected_resume_at. */
export async function pauseLiveSession(
  sessionId: string | number,
  expectedResumeAt: string | Date,
) {
  const iso =
    expectedResumeAt instanceof Date
      ? expectedResumeAt.toISOString()
      : String(expectedResumeAt || "").trim();
  if (!iso) {
    throw new Error("Expected resume date and time is required to pause.");
  }
  try {
    const response = await authService.post(
      `/session/sessions/${encodeURIComponent(String(sessionId))}/pause/`,
      { expected_resume_at: iso },
    );
    const data = response.data as { success?: boolean; message?: string };
    const message = String(data?.message || "").trim();
    if (data?.success === true || /successfully/i.test(message)) {
      return data;
    }
    throw new Error(message || "Failed to pause session");
  } catch (err: unknown) {
    const detail = (err as { response?: { data?: { detail?: unknown } } })
      ?.response?.data?.detail;
    if (typeof detail === "string" && detail.trim()) {
      throw new Error(detail.trim());
    }
    if (detail && typeof detail === "object" && detail !== null) {
      const msg = (detail as { message?: unknown }).message;
      if (typeof msg === "string" && msg.trim()) {
        throw new Error(msg.trim());
      }
    }
    throw err;
  }
}
